const RenderMgr = require('../RenderMgr');
const AppEvent = require('../../misc/AppEvent');
const ElementRenderer = require('./ElementRenderer');
const FeatureRenderer = require('./FeatureRenderer');
const PixelClusterRenderer = require('./PixelClusterRenderer');
const PixelClusterRendererG2 = require('./PixelClusterRendererG2');
const WMSRenderer = require('./WMSRenderer');
const GeometryBuilder = require('../../utils/builder/GeometryBuilder');
const SymbolBuilder = require('../../utils/builder/SymbolBuilder');
const FeatureBuilder = require('../../utils/builder/FeatureBuilder');
const g2 = require('../../g2');
/**
 * @constructor
 * @classdesc 业务数据简单上图管理器
 * @augments G.render.RenderMgr
 * @alias G.render.SimpleRenderMgr
 * @param {Object} options  构造参数；
 * @param {G.Map} options.map  地图对象
 * @param {G.Map} options.layerZIndex {Number}
 * @example
 * //使用示例代码
 * //todo
 */
let component = RenderMgr.extend({
    options: {
        layerZIndex: 1,
    },
    initialize(options) {
        RenderMgr.prototype.initialize.call(this, options);
        //
        this._last_mouse_over = null;
    },
    
    /**
     * @inheritDoc
     */
    addListeners() {
        this.map.listen('click', this._clickHandler, this);
        this.map.listen('mousemove', this._mouseMoveHandler, this);
    },
    /**
     * @inheritDoc
     */
    removeListeners() {
        this.map.off('click', this._clickHandler, this);
        this.map.off('mousemove', this._mouseMoveHandler, this);
    },
    
    /**
     * 添加一类业务数据
     * @method
     * @memberOf  G.render.SimpleRenderMgr.prototype
     * @param opts {Object}
     * @param opts.featureType {String} 业务数据类型标识
     * @param opts.featureName {String} 业务数据类型名称
     * @param opts.list {Array} 业务数据列表
     * @param opts.idField {String} 数据标识属性名
     * @param opts.geometryBuilder {G.utils.GeometryBuilder} 坐标构建器
     * @param opts.symbolBuilder {G.utils.SymbolBuilder} 符号配置
     * @param [opts.type] {Integer} 0=元素渲染，渲染少量数据（<5000）； 1=要素渲染，大数据量（>5000）； 2 = 像素聚合；3 = g2聚合；默认为0；
     * type=2时，symbolBuilder返回参数参考GV 点聚合PointCluster说明 {@link http://172.18.24.165/developer/guide/PointClusterChart1.html}
     * @param [opts.listeners] {Object} 事件监听
     *
     **/
    add(opts) {
        this._checkLoaded();
        this.remove(opts.featureType);
        //
        let featureTypeObj = this._getFeatureType(opts.featureType) || {};
        let rendererClazz = this._getRendererClazz(opts.type);
        let geometryBuilder = opts.geometryBuilder || new GeometryBuilder({});
        let symbolBuilder = opts.symbolBuilder || new SymbolBuilder({});
        let featureBuilder = new FeatureBuilder({
            idField: opts.idField,
            geometryBuilder: geometryBuilder,
            symbolBuilder: symbolBuilder,
        });
        let renderer = featureTypeObj.renderer || new rendererClazz({
                id: opts.featureType,
                name: opts.featureName,
                map: this.map,
                idField: opts.idField,
                geometryType: geometryBuilder.getGeometryType(),
                featureBuilder: featureBuilder,
                layer: opts.layer || null,
                eventDispatcher: this.eventDispatcher,
                zIndex: this.options.layerZIndex
            });
        renderer.add(opts.list);
        //
        featureTypeObj.featureType = opts.featureType;
        featureTypeObj.featureName = opts.featureName;
        featureTypeObj.idField = opts.idField;
        featureTypeObj.geometryBuilder = geometryBuilder;
        featureTypeObj.symbolBuilder = symbolBuilder;
        featureTypeObj.renderer = renderer;
        featureTypeObj.listeners = opts.listeners || {};
        this.featureTypeList.push(featureTypeObj);
        // 
        this._fireUpdateEvent();
    },
    
    addWMS(opts) {
        this._checkLoaded();
        this.remove(opts.featureType);
        let geometryBuilder = opts.geometryBuilder || new GeometryBuilder({geometryType: opts.geometryType});
        let symbolBuilder = opts.symbolBuilder || new SymbolBuilder({});
        let featureBuilder = new FeatureBuilder({
            idField: opts.idField,
            geometryBuilder: geometryBuilder,
            symbolBuilder: symbolBuilder,
        });
        let featureTypeObj = this._getFeatureType(opts.featureType) || {};
        let renderer = new WMSRenderer({
            id: opts.featureType,
            name: opts.featureName,
            map: this.map,
            layer: opts.layer || null,
            eventDispatcher: this.eventDispatcher,
            zIndex: this.options.layerZIndex,
            layerOptions: opts.layerOptions,
            geometryType: opts.geometryType,
            featureBuilder,
        });
        // renderer.add(opts.list);
        //
        featureTypeObj.featureType = opts.featureType;
        featureTypeObj.featureName = opts.featureName;
        featureTypeObj.geometryBuilder = geometryBuilder;
        featureTypeObj.idField = opts.idField;
        featureTypeObj.renderer = renderer;
        featureTypeObj.listeners = opts.listeners || {};
        this.featureTypeList.push(featureTypeObj);
        //
        this._fireUpdateEvent();
    },
    //
    _getRendererClazz(type) {
        type = type || 0;
        let typeMap = {
            0: ElementRenderer,
            1: FeatureRenderer,
            2: PixelClusterRenderer,
            3: PixelClusterRendererG2
        }
        return typeMap[type] || null;
    },
    
    /**
     * 更新数据
     * @ignore
     * @method
     * @memberOf  G.render.SimpleRenderMgr.prototype
     * @param opts {Object}
     * @param opts.featureType {String} 业务数据类型标识
     * @param opts.list {Array} 业务数据列表
     */
    update(opts) {
        this._checkLoaded();
        //
        let featureTypeObj = this._getFeatureType(opts.featureType);
        if (featureTypeObj) {
            featureTypeObj.renderer.update(opts.list);
            // 
            this._fireUpdateEvent();
        }
    },
    
    
    /**
     * 获取业务类型对应的图层对象
     * @method
     * @memberOf  G.render.SimpleRenderMgr.prototype
     * @param featureType {String} 业务数据类型标识
     * @returns {g2.carto.ILayer}
     */
    getLayer(featureType) {
        let featureTypeObj = this._getFeatureType(featureType);
        if (featureTypeObj) {
            return featureTypeObj.renderer.getLayer();
        }
    },
    
    /**
     * 遍历数据
     * @inheritdoc
     */
    visitFeature(featureType, visitor) {
        const layer = this.getLayer(featureType);
        if (layer && visitor) {
            const visitLayer = (l, visitorObj) => {
                let flag = true;
                if (l instanceof g2.carto.GroupLayer) {
                    for (const l1 of l.groupLayers) {
                        if(!visitLayer(l1, visitorObj)){
                            flag = false;
                            break;
                        }
                    }
                } else if (l instanceof g2.carto.ElementLayer || l instanceof g2.carto.ClusterLayer
                    || l instanceof g2.carto.FeatureLayer) {
                    for (let i = 0; i < l.getCount(); i++) {
                        const ele = l.get(i);
                        if(!visitorObj.visit(ele, l)){
                            flag = false;
                            break;
                        }
                    }
                }
                return flag;
            };
            visitLayer(layer, visitor);
        }
    },
    
    /**
     * 获取数据范围
     * @method
     * @memberOf  G.render.SimpleRenderMgr.prototype
     * @param featureType
     * @returns {g2.sfs.Geometry}
     */
    getExtent(featureType){
        let featureTypeObj = this._getFeatureType(featureType);
        if (featureTypeObj) {
            return featureTypeObj.renderer.getExtent();
        }
    },
    /**
     *
     * @param event
     * @private
     */
    async _clickHandler(event) {
        if (this.listens('click') || this.listens('noclick')) {
            // console.debug('click', event);
            filter = function (event, obj){
                return obj;
            };
            let hitTestList = await this._hitTest(event.screenX, event.screenY, 'click', filter);
            if (hitTestList.length > 0) {
                this._invokeFeatureListeners('click', hitTestList);
                this.fire('click', {
                    list: hitTestList,
                    event,
                });
            } else {
                this._invokeFeatureListeners('noclick');
                this.fire('noclick');
            }
        }
    },
    /**
     *
     * @param event
     * @private
     */
     async _mouseMoveHandler(event) {
        if (this.listens('mouseover') || this.listens('mouseout')) {
            // console.debug('mousemove', event);
            filter = function (event, obj){
                if (obj.renderer instanceof WMSRenderer) {
                    return null;
                } else {
                    return obj;
                }
            };
            let hitTestList = await this._hitTest(event.screenX, event.screenY, 'mousemove', filter);
            if (hitTestList.length > 0) {
                let key = this._getHitTargetKey(hitTestList);
                if (this._last_mouse_over == null || this._last_mouse_over.key != key) {
                    this._invokeFeatureListeners('mouseover', hitTestList);
                    this.fire('mouseover', {
                        list: hitTestList,
                        event,
                    });
                    this._last_mouse_over = {
                        key: key,
                        type: key.split('~')[1]
                    };
                }
            } else {
                if (this._last_mouse_over) {
                    //
                    let featureTypeObj = this._getFeatureType(this._last_mouse_over.type);
                    if (featureTypeObj && featureTypeObj.listeners['mouseout']) {
                        featureTypeObj.listeners['mouseout']();
                    }
                    //
                    this.fire('mouseout', {
                        event,
                    });
                    this._last_mouse_over = null;
                }
            }
        }
    },
    
    /**
     * 触发每类数据的监听
     * @param {String} eventName
     * @param {Array} hitTestList
     */
    _invokeFeatureListeners(eventName, hitTestList) {
        let featureGroup = {};
        if (hitTestList) {
            for (let i = 0; i < hitTestList.length; i++) {
                let item = hitTestList[i];
                featureGroup[item.featureType] = featureGroup[item.featureType] || [];
                featureGroup[item.featureType].push(item);
            }
            for (let featureType in featureGroup) {
                let featureTypeObj = this._getFeatureType(featureType);
                if (featureTypeObj && featureTypeObj.listeners[eventName]) {
                    featureTypeObj.listeners[eventName](featureGroup[featureType]);
                }
            }
        } else {
            for (let i = 0; i < this.featureTypeList.length; i++) {
                let featureTypeObj = this.featureTypeList[i];
                if (featureTypeObj && featureTypeObj.listeners[eventName]) {
                    featureTypeObj.listeners[eventName]();
                }
            }
        }
    },
    //点中的唯一标识
    _getHitTargetKey(list) {
        let key = null;
        if (list.length > 0) {
            let obj = list[0];
            key = [obj.elementId, obj.featureType].join('~');
        }
        return key;
    },
    /**
     *
     * @private
     */
    async _hitTest(screenX, screenY, event, filter) {
        let featureTypeList = [];
        // 按点线面顺序排序，且后加载的在上
        let pointList = [], polylineList = [], polygonList = [];
        for (let i = this.featureTypeList.length - 1; i >= 0; i--) {
            let obj_ = this.featureTypeList[i];
            let obj = filter(event, obj_);
            if (obj) {
                let geometryType = obj.geometryBuilder.getGeometryType();
                switch (geometryType.toLowerCase()) {
                    case 'point':
                        pointList.push(obj);
                        break;
                    case 'polyline':
                        polylineList.push(obj);
                        break;
                    case 'polygon':
                        polygonList.push(obj);
                        break;
                }
            }
        }
        featureTypeList = pointList.concat(polylineList, polygonList);
        return new Promise(async (resolve, reject) => {
            let list = [];
            for (let i = 0; i < featureTypeList.length; i++) {
                let featureTypeObj = featureTypeList[i];
                await this._hitTestFeatureType(event, featureTypeObj, featureTypeObj.renderer.getLayer(), list, screenX, screenY)
            }
            resolve(list);
        })
        
    },
    /**
     * 测试图层点中
     * @ignore
     * @param featureTypeObj
     * @param layer
     * @param array
     * @param screenX
     * @param screenY
     * @private
     * @returns {Object} {
     *  element:a,
     *  elementId:b,
     *  layerId:c,
     *  featureType:d
     * }
     *
     */
    _hitTestFeatureType (event, featureTypeObj, layer, array, screenX, screenY, groupLayerId) {
        let l = layer;
        let getElementId = function (element) {
            let id;
            if (element instanceof g2.sfs.Element) {
                id = element.id;
            } else if (element instanceof g2.sfs.Feature) {
                id = element.objectId;
            }
            return id;
        }
        const visitHitResult = function (hitResult, featureTypeObj, array) {
            if (hitResult.graphiclist.length > 0) {
                for (var i = 0; i < hitResult.graphiclist.length; i++) {
                    var ele = hitResult.graphiclist[i];
                    if (ele && (ele.element || ele.feature || ele.properties)) {
                        var object = {};
                        object.element = ele.element || ele.feature || ele;
                        object.elementId = getElementId(object.element);
                        object.layerId = l.getLayerId() || groupLayerId;
                        object.featureType = featureTypeObj.featureType;
                        array.push(object);
                    }
                }
            }
        }
        return new Promise(async (resolve, reject) => {
            if (l &&
                (l instanceof g2.carto.ElementLayer ||
                l instanceof g2.carto.FeatureLayer ||
                l instanceof g2.carto.ClusterLayer ||
                l instanceof g2.carto.GroupLayer ||
                l instanceof g2.carto.TileLayer) &&
                l.visible) {
                l.map = l.map || this.map;
                groupLayerId = groupLayerId || l.getLayerId();
                if (l instanceof g2.carto.GroupLayer) {
                    let groupLayers = l.groupLayers;
                    for (var k = groupLayers.length - 1; k >= 0; k--) {
                        var groupedLayer = groupLayers[k];
                        await this._hitTestFeatureType(event, featureTypeObj, groupedLayer, array, screenX, screenY, l.getLayerId());
                    }
                } else {
                    if (l instanceof g2.carto.ClusterLayer) { // 像素聚合图层单独处理
                        const hitResult = l.hitTestList(screenX, screenY);
                        const clusterStyle = featureTypeObj.symbolBuilder.buildClusterStyle();
                        const clickScatter = event === 'click' && clusterStyle.clusterScatterOnClick;
                        const mouseMoveScatter = event == 'mousemove' && clusterStyle.clusterScatterOnMousemove;
                        if (hitResult.graphiclist.length > 0 &&
                            (hitResult.radius == undefined || hitResult.radius == 0)) {// 点击的是散点图标
                            visitHitResult(hitResult, featureTypeObj, array);
                            if (clickScatter || mouseMoveScatter) { // 清理
                                l.hitTestListScatter(screenX, screenY);
                            }
                            resolve(array);
                        } else {
                            let hitResultScatter = null;
                            if (clickScatter) { // 点击散开
                                console.debug('click scatter');
                                if (l.hitTestListScatter) {
                                    hitResultScatter = l.hitTestListScatter(screenX, screenY);
                                }
                            } else if (mouseMoveScatter) { // 鼠标滑过，散开
                                console.debug('mousemove scatter');
                                if (l.hitTestListScatter) {
                                    hitResultScatter = l.hitTestListScatter(screenX, screenY);
                                }
                            }
                            if (hitResultScatter && (hitResultScatter.radius == undefined || hitResultScatter.radius == 0 )) {
                                visitHitResult(hitResultScatter, featureTypeObj, array);
                            }
                        }
                    } else if (l instanceof g2.carto.TileLayer) {// wms图层
                        const renderer = featureTypeObj.renderer;
                        const featureList = await renderer.hitTest(screenX, screenY);
                        if (featureList && featureList.length > 0) {
                            visitHitResult({graphiclist: featureList}, featureTypeObj, array);
                        }
                    } else {
                        const hitResult = l.hitTestList(screenX, screenY);
                        visitHitResult(hitResult, featureTypeObj, array);
                    }
                }
            }
            resolve();
        })
    },
});
module.exports = component;